import type { AppRole } from '@tabel/shared/schemas/auth';
import { Search, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROLE_LABEL, homeOf, useRole } from '../lib/role';
import { useUnsavedGuard } from './UnsavedGuard';
import { Dropdown, MenuItem, SwitcherButton } from './ui';

/**
 * Роль вверху экрана (FR-002; дизайн-система, README «Каркас»: служебное справа — свежесть данных и роль). Роль — это
 * переключатель контекста (README, «Переключатель контекста»; `SwitcherButton`, `Dropdown`): кнопка с рамкой, иконкой и
 * ⇅ открывает список с поиском и галочкой у текущей роли. Роль одна — кнопка та же, список прямо говорит, что других
 * ролей на объекте нет. Сегменты (`Segmented`) — не для контекста: они для периода, вида и фильтра. Роли — из
 * `/api/me` (004, research R18). Смена роли — через защиту несохранённого, затем домашняя страница роли (`homeOf`).
 */
export function RoleSwitch({ roles }: { roles: readonly AppRole[] }) {
  const { role, setRole } = useRole();
  const navigate = useNavigate();
  const { guard } = useUnsavedGuard();
  const [q, setQ] = useState('');
  if (roles.length === 0) return null;
  const needle = q.trim().toLowerCase();
  const found = roles.filter((r) => ROLE_LABEL[r].toLowerCase().includes(needle));
  const pick = (r: AppRole) => {
    if (r === role) return;
    guard(() => {
      setRole(r);
      navigate(homeOf(r));
    });
  };

  return (
    <Dropdown
      align="end"
      width="w-[272px]"
      trigger={({ open, toggle }) => (
        <SwitcherButton
          compact
          icon={UserRound}
          caption="Роль"
          label={ROLE_LABEL[role]}
          open={open}
          onClick={() => {
            setQ('');
            toggle();
          }}
        />
      )}
    >
      {(close) => (
        <>
          <div className="-mt-1 flex items-center gap-2 border-b border-line px-3">
            <Search size={16} strokeWidth={1.75} className="text-ink-3" aria-hidden />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Найти роль…"
              aria-label="Найти роль"
              className="h-10 min-w-0 flex-1 bg-transparent text-[13.5px] text-ink placeholder:text-ink-3 focus:outline-none max-sm:text-base"
            />
          </div>
          <div className="max-h-[50dvh] overflow-y-auto py-1">
            {found.length > 0 ? (
              found.map((r) => (
                <MenuItem
                  key={r}
                  checked={r === role}
                  onClick={() => {
                    close();
                    pick(r);
                  }}
                >
                  {ROLE_LABEL[r]}
                </MenuItem>
              ))
            ) : (
              <p className="px-3 py-2 text-[13px] text-ink-3">Роль не найдена.</p>
            )}
          </div>
          {roles.length === 1 && (
            <p className="border-t border-line px-3 pt-2 pb-1.5 text-[12px] text-ink-3">
              Других ролей на этом объекте нет. Роли назначает администратор системы.
            </p>
          )}
        </>
      )}
    </Dropdown>
  );
}
